import { supabase } from '../config/supabase';
import { formatCurrency } from '../utils/currency';
import { repairOrdersService } from './repairOrdersService';
import { contactsService } from './contactsService';

export interface RepairInvoice {
  id: string;
  business_id: string;
  repair_invoice_number: string;
  repair_order_id: string;
  repair_order?: any; // Will include repair order details
  customer?: any; // Customer from repair order

  // Invoice Details
  invoice_date: string;
  description?: string;

  // Financial Details
  subtotal: number;
  tax_total: number;
  total_amount: number;

  // Status
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';

  // Metadata
  created_at: string;
  updated_at: string;
}

export interface RepairInvoiceItem {
  id: string;
  repair_invoice_id: string;
  description: string;
  quantity: number;
  unit_price: number;
  tax_rate: number;
  tax_amount: number;
  subtotal: number;
  created_at: string;
}

export interface RepairInvoiceFormData {
  repair_invoice_number?: string;
  repair_order_id: string;
  invoice_date: string;
  description?: string;
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';
  items: RepairInvoiceItemFormData[];
}

export interface RepairInvoiceItemFormData {
  description: string;
  quantity: number;
  unit_price: number;
  tax_rate: number;
}

// Type for repair invoice with items
export interface RepairInvoiceWithItems extends RepairInvoice {
  items: RepairInvoiceItem[];
}

export const repairInvoicesService = {
  // Generate repair invoice number (RI-XXXXXX format)
  async generateRepairInvoiceNumber(): Promise<string> {
    try {
      // Call the database function to generate the number
      const { data, error } = await supabase.rpc('generate_repair_invoice_number');

      if (error) throw error;
      return data || 'RI-000001'; // Fallback if function fails
    } catch (error) {
      console.error('Error generating repair invoice number:', error);
      // Fallback: generate based on timestamp
      const timestamp = Date.now().toString().slice(-6);
      return `RI-${timestamp.padStart(6, '0')}`;
    }
  },

  // Get all repair invoices for the business
  async getRepairInvoices(businessId?: string): Promise<{ invoices: RepairInvoice[]; error: Error | null }> {
    try {
      let query = supabase
        .from('repair_invoices')
        .select(`
          *,
          repair_order:repair_orders (
            id,
            repair_order_number,
            customer:contacts (*),
            device_brand,
            device_model,
            device_serial,
            device_problem
          )
        `)
        .order('created_at', { ascending: false });

      // Filter by business_id if provided
      if (businessId) {
        query = query.eq('business_id', businessId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { invoices: data || [], error: null };
    } catch (error) {
      return { invoices: [], error: error as Error };
    }
  },

  // Get repair invoice by ID with items
  async getRepairInvoiceById(id: string, businessId?: string): Promise<{ invoice: RepairInvoiceWithItems | null; error: Error | null }> {
    try {
      console.log(`Fetching repair invoice by ID: ${id}, businessId: ${businessId}`);

      let query = supabase
        .from('repair_invoices')
        .select(`
          *,
          repair_order:repair_orders (
            id,
            repair_order_number,
            customer:contacts (*),
            device_brand,
            device_model,
            device_serial,
            device_problem
          )
        `)
        .eq('id', id);

      // Filter by business_id if provided for security
      if (businessId) {
        query = query.eq('business_id', businessId);
      }

      const { data: invoiceData, error: invoiceError } = await query.single();

      if (invoiceError) {
        console.error('Error fetching repair invoice:', invoiceError);
        throw invoiceError;
      }
      if (!invoiceData) {
        console.log('Repair invoice not found');
        throw new Error('Repair invoice not found');
      }

      console.log('Repair invoice data:', invoiceData);

      // Get the items for this repair invoice
      console.log('Fetching items for repair invoice:', id);
      const { data: itemsData, error: itemsError } = await supabase
        .from('repair_invoice_items')
        .select('*')
        .eq('repair_invoice_id', id)
        .order('created_at', { ascending: true });

      if (itemsError) {
        console.error('Error fetching repair invoice items:', itemsError);
        throw itemsError;
      }

      console.log('Raw items data:', itemsData);

      // Combine invoice and items
      const invoiceWithItems = {
        ...invoiceData,
        items: itemsData || []
      };

      console.log('Final invoice with items:', invoiceWithItems);
      return { invoice: invoiceWithItems, error: null };
    } catch (error) {
      console.error('Error in getRepairInvoiceById:', error);
      return { invoice: null, error: error as Error };
    }
  },

  // Get completed repair orders for invoice creation
  async getCompletedRepairOrders(businessId?: string): Promise<{ orders: any[]; error: Error | null }> {
    try {
      let query = supabase
        .from('repair_orders')
        .select(`
          *,
          customer:contacts (*)
        `)
        .eq('status', 'completed')
        .order('created_at', { ascending: false });

      // Filter by business_id if provided
      if (businessId) {
        query = query.eq('business_id', businessId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { orders: data || [], error: null };
    } catch (error) {
      return { orders: [], error: error as Error };
    }
  },

  // Create a new repair invoice
  async createRepairInvoice(invoiceData: RepairInvoiceFormData, businessId?: string): Promise<{ invoice: RepairInvoice | null; error: Error | null }> {
    try {
      if (!businessId) {
        throw new Error('Business ID is required');
      }

      // Use provided repair invoice number or generate one
      const repairInvoiceNumber = invoiceData.repair_invoice_number || await this.generateRepairInvoiceNumber();

      // Validate that repair order exists and is completed
      const { order: repairOrder, error: orderError } = await repairOrdersService.getRepairOrderById(invoiceData.repair_order_id, businessId);
      if (orderError) throw orderError;
      if (!repairOrder) throw new Error('Repair order not found');
      if (repairOrder.status !== 'completed') throw new Error('Only completed repair orders can be invoiced');

      // Check if repair order is already invoiced
      const { data: existingInvoice, error: checkError } = await supabase
        .from('repair_invoices')
        .select('id')
        .eq('repair_order_id', invoiceData.repair_order_id)
        .eq('business_id', businessId)
        .single();

      if (existingInvoice) {
        throw new Error('This repair order has already been invoiced');
      }

      // Calculate totals
      let subtotal = 0;
      let taxTotal = 0;
      const processedItems = invoiceData.items.map(item => {
        const itemSubtotal = item.quantity * item.unit_price;
        const itemTaxAmount = itemSubtotal * (item.tax_rate / 100);
        subtotal += itemSubtotal;
        taxTotal += itemTaxAmount;

        return {
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price,
          tax_rate: item.tax_rate,
          tax_amount: itemTaxAmount,
          subtotal: itemSubtotal,
        };
      });

      const totalAmount = subtotal + taxTotal;

      // Create the repair invoice
      const { data: invoice, error: invoiceError } = await supabase
        .from('repair_invoices')
        .insert([
          {
            business_id: businessId,
            repair_invoice_number: repairInvoiceNumber,
            repair_order_id: invoiceData.repair_order_id,
            invoice_date: invoiceData.invoice_date,
            description: invoiceData.description,
            subtotal: subtotal,
            tax_total: taxTotal,
            total_amount: totalAmount,
            status: invoiceData.status,
          },
        ])
        .select()
        .single();

      if (invoiceError) throw invoiceError;
      if (!invoice) throw new Error('Failed to create repair invoice');

      // Create the repair invoice items
      if (processedItems.length > 0) {
        const itemsToInsert = processedItems.map(item => ({
          repair_invoice_id: invoice.id,
          ...item,
        }));

        const { error: itemsError } = await supabase
          .from('repair_invoice_items')
          .insert(itemsToInsert);

        if (itemsError) throw itemsError;
      }

      // The database triggers will automatically:
      // 1. Update customer balance
      // 2. Set repair order status to "invoiced"

      return { invoice, error: null };
    } catch (error) {
      return { invoice: null, error: error as Error };
    }
  },

  // Update an existing repair invoice
  async updateRepairInvoice(id: string, invoiceData: RepairInvoiceFormData, businessId?: string): Promise<{ invoice: RepairInvoice | null; error: Error | null }> {
    try {
      if (!businessId) {
        throw new Error('Business ID is required');
      }

      // Calculate totals
      let subtotal = 0;
      let taxTotal = 0;
      const processedItems = invoiceData.items.map(item => {
        const itemSubtotal = item.quantity * item.unit_price;
        const itemTaxAmount = itemSubtotal * (item.tax_rate / 100);
        subtotal += itemSubtotal;
        taxTotal += itemTaxAmount;

        return {
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price,
          tax_rate: item.tax_rate,
          tax_amount: itemTaxAmount,
          subtotal: itemSubtotal,
        };
      });

      const totalAmount = subtotal + taxTotal;

      // Update the repair invoice
      const { data: invoice, error: invoiceError } = await supabase
        .from('repair_invoices')
        .update({
          repair_invoice_number: invoiceData.repair_invoice_number,
          invoice_date: invoiceData.invoice_date,
          description: invoiceData.description,
          subtotal: subtotal,
          tax_total: taxTotal,
          total_amount: totalAmount,
          status: invoiceData.status,
        })
        .eq('id', id)
        .eq('business_id', businessId)
        .select()
        .single();

      if (invoiceError) throw invoiceError;

      // Delete existing items
      const { error: deleteError } = await supabase
        .from('repair_invoice_items')
        .delete()
        .eq('repair_invoice_id', id);

      if (deleteError) throw deleteError;

      // Create the new repair invoice items
      if (processedItems.length > 0) {
        const itemsToInsert = processedItems.map(item => ({
          repair_invoice_id: id,
          ...item,
        }));

        const { error: itemsError } = await supabase
          .from('repair_invoice_items')
          .insert(itemsToInsert);

        if (itemsError) throw itemsError;
      }

      return { invoice, error: null };
    } catch (error) {
      return { invoice: null, error: error as Error };
    }
  },

  // Delete a repair invoice
  async deleteRepairInvoice(id: string, businessId?: string): Promise<{ error: Error | null }> {
    try {
      if (!businessId) {
        throw new Error('Business ID is required');
      }

      // Get the invoice to restore customer balance
      const { invoice, error: fetchError } = await this.getRepairInvoiceById(id, businessId);
      if (fetchError) throw fetchError;
      if (!invoice) throw new Error('Repair invoice not found');

      // Only allow deletion of draft invoices
      if (invoice.status !== 'draft') {
        throw new Error('Only draft repair invoices can be deleted');
      }

      // Delete the repair invoice items first
      const { error: itemsError } = await supabase
        .from('repair_invoice_items')
        .delete()
        .eq('repair_invoice_id', id);

      if (itemsError) throw itemsError;

      // Delete the repair invoice
      const { error: invoiceError } = await supabase
        .from('repair_invoices')
        .delete()
        .eq('id', id)
        .eq('business_id', businessId);

      if (invoiceError) throw invoiceError;

      // Note: Customer balance restoration and repair order status change
      // would need to be handled manually or through database triggers
      // For now, we'll leave the repair order as "invoiced" and customer balance as is

      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  },

  // Update repair invoice status
  async updateRepairInvoiceStatus(
    id: string,
    status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled',
    businessId?: string
  ): Promise<{ invoice: RepairInvoice | null; error: Error | null }> {
    try {
      if (!businessId) {
        throw new Error('Business ID is required');
      }

      const { data: invoice, error } = await supabase
        .from('repair_invoices')
        .update({ status })
        .eq('id', id)
        .eq('business_id', businessId)
        .select()
        .single();

      if (error) throw error;
      return { invoice, error: null };
    } catch (error) {
      return { invoice: null, error: error as Error };
    }
  },

  // Get repair invoice formatted total
  getFormattedTotal(amount: number): string {
    return formatCurrency(amount);
  },

  // Get repair invoice status label
  getStatusLabel(status: string): string {
    const labels: Record<string, string> = {
      draft: 'Draft',
      sent: 'Sent',
      paid: 'Paid',
      overdue: 'Overdue',
      cancelled: 'Cancelled',
    };
    return labels[status] || status;
  },

  // Get repair invoice status badge class
  getStatusBadgeClass(status: string): string {
    const classes: Record<string, string> = {
      draft: 'badge-secondary',
      sent: 'badge-info',
      paid: 'badge-success',
      overdue: 'badge-error',
      cancelled: 'badge-error',
    };
    return classes[status] || 'badge';
  },
};